/**
 * Copy Day Service
 * Copies bookings from a source day to a target date range
 */

import { ViewBooking } from "collins-js-sdk";
import dayjs from "dayjs";
import { addOrUpdateBooking } from "../../APIs/Booking/UpdateBooking";
import { DiaryContextType } from "../../context/DiaryContext";
import { getRandomStatus, getRandomStage, generateId } from "./templateData";
import { BookingStage } from "../../types/venues";

/**
 * Map random status string to booking status
 */
const mapRandomStatusToBookingStatus = (
  randomStatus: string
): "new" | "complete" | "in_progress" => {
  if (randomStatus === "new") {
    return "new";
  }
  if (randomStatus === "complete" || randomStatus === "confirmed") {
    return "complete";
  }
  return "in_progress";
};

/**
 * Randomise booking states if requested
 */
const randomiseBookingStates = (
  newBooking: ViewBooking,
  bookingStages: BookingStage[]
): void => {
  const randomStatus = getRandomStatus();
  newBooking.status = mapRandomStatusToBookingStatus(randomStatus);

  newBooking.currentStage = getRandomStage(
    randomStatus,
    bookingStages.map((s) => s.id)
  );

  // Clear stage times if status changed
  (newBooking as any).stageTimes = undefined;
  (newBooking as any).emailsSent = undefined;
  (newBooking as any).smsSent = undefined;
  newBooking.doubleConfirmed = undefined;
};

/**
 * Process deposits for copied booking
 */
const processDeposits = (
  newBooking: ViewBooking,
  includeDeposits: boolean
): void => {
  if (!includeDeposits) {
    (newBooking as any).deposits = [];
    return;
  }

  if ((newBooking as any).deposits) {
    (newBooking as any).deposits = (newBooking as any).deposits.map(
      (deposit: any) => ({
        ...deposit,
        id: generateId(),
        paymentRef: `DEP-${generateId()}`,
        date: new Date()
      })
    );
  }
};

/**
 * Process preorders for copied booking
 */
const processPreorders = (
  newBooking: ViewBooking,
  includePreorders: boolean
): void => {
  if (!includePreorders) {
    (newBooking as any).preorders = [];
    return;
  }

  if ((newBooking as any).preorders) {
    (newBooking as any).preorders = (newBooking as any).preorders.map(
      (preorder: any) => ({
        ...preorder,
        id: generateId(),
        createdDate: new Date(),
        items: preorder.items?.map((item: any) => ({
          ...item,
          id: generateId()
        }))
      })
    );
  }
};

/**
 * Process comments for copied booking
 */
const processComments = (
  newBooking: ViewBooking,
  includeComments: boolean
): void => {
  if (!includeComments) {
    newBooking.comments = [];
    (newBooking as any).adminNotes = [];
    return;
  }

  if (newBooking.comments) {
    newBooking.comments = newBooking.comments.map((comment) => ({
      ...comment,
      time: new Date().toISOString()
    }));
  }

  if ((newBooking as any).adminNotes) {
    (newBooking as any).adminNotes = (newBooking as any).adminNotes.map(
      (note: any) => ({
        ...note,
        time: new Date()
      })
    );
  }
};

/**
 * Clear transactional data from copied booking
 */
const clearTransactionalData = (newBooking: ViewBooking): void => {
  if ((newBooking as any).groupMembers) {
    (newBooking as any).groupMembers = (newBooking as any).groupMembers.map(
      (member: any) => ({
        ...member,
        id: generateId()
      })
    );
  }

  (newBooking as any).notifications = [];
  (newBooking as any).invoices = [];
  (newBooking as any).refunds = [];
  (newBooking as any).invoiced = false;
  (newBooking as any).reconciled = false;
};

export type CopyDayOptions = {
  randomiseStates: boolean;
  includeDeposits: boolean;
  includePreorders: boolean;
  includeDietaryRequirements: boolean;
  includeNotes: boolean;
  includeComments: boolean;
};

export type CopyDayProgress = {
  current: number;
  total: number;
  currentDate: string;
  currentBooking: string;
  status: "pending" | "fetching" | "copying" | "complete" | "error";
  error?: string;
};

const DEFAULT_OPTIONS: CopyDayOptions = {
  randomiseStates: false,
  includeDeposits: true,
  includePreorders: true,
  includeDietaryRequirements: true,
  includeNotes: true,
  includeComments: true
};

/**
 * Copy bookings from a source day to a target date range
 */
export const copyDayBookings = async (
  sourceBookings: ViewBooking[],
  diaryContext: DiaryContextType,
  startDate: Date,
  endDate: Date,
  options: Partial<CopyDayOptions> = {},
  onProgress?: (progress: CopyDayProgress) => void
): Promise<{ success: boolean; bookingsCreated: number; errors: string[] }> => {
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options };
  const errors: string[] = [];
  let bookingsCreated = 0;

  if (sourceBookings.length === 0) {
    return {
      success: false,
      bookingsCreated: 0,
      errors: ["No bookings found on source day to copy"]
    };
  }

  const venueConfig = diaryContext.venueConfig;
  if (!venueConfig) {
    return {
      success: false,
      bookingsCreated: 0,
      errors: ["No venue configuration found"]
    };
  }

  const bookingStages = venueConfig.booking_stages ?? [];

  const currentDate = dayjs(startDate);
  const end = dayjs(endDate);
  const totalDays = end.diff(currentDate, "day") + 1;
  const totalBookings = totalDays * sourceBookings.length;

  let current = 0;

  // Iterate through each day
  let iterDate = currentDate;
  while (iterDate.isBefore(end) || iterDate.isSame(end, "day")) {
    // Copy each booking from the source day
    for (const sourceBooking of sourceBookings) {
      current++;

      const bookingName =
        `${sourceBooking.firstName ?? ""} ${sourceBooking.lastName ?? ""}`.trim() ||
        "Unknown";

      onProgress?.({
        current,
        total: totalBookings,
        currentDate: iterDate.format("YYYY-MM-DD"),
        currentBooking: bookingName,
        status: "copying"
      });

      try {
        const newBooking = createCopiedBooking(
          sourceBooking,
          iterDate.toDate(),
          bookingStages,
          mergedOptions
        );

        await addOrUpdateBooking({ booking: newBooking, diaryContext });
        bookingsCreated++;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        errors.push(
          `Failed to copy booking for ${bookingName} to ${iterDate.format("YYYY-MM-DD")}: ${errorMessage}`
        );
      }
    }

    iterDate = iterDate.add(1, "day");
  }

  onProgress?.({
    current: totalBookings,
    total: totalBookings,
    currentDate: end.format("YYYY-MM-DD"),
    currentBooking: "",
    status: errors.length === 0 ? "complete" : "error",
    error: errors.length > 0 ? `${errors.length} errors occurred` : undefined
  });

  return {
    success: errors.length === 0,
    bookingsCreated,
    errors
  };
};

/**
 * Create a copy of a booking for a new date
 */
const createCopiedBooking = (
  sourceBooking: ViewBooking,
  targetDate: Date,
  bookingStages: BookingStage[],
  options: CopyDayOptions
): ViewBooking => {
  // Create a deep copy of the source booking
  const newBooking: ViewBooking = {
    ...sourceBooking,
    id: undefined, // Will be assigned by the API
    date: targetDate,
    createdDate: undefined,
    lastUpdated: undefined
  };

  // Randomise states if requested
  if (options.randomiseStates) {
    randomiseBookingStates(newBooking, bookingStages);
  }

  // Process deposits and preorders
  processDeposits(newBooking, options.includeDeposits);
  processPreorders(newBooking, options.includePreorders);

  // Remove dietary requirements if not included
  if (!options.includeDietaryRequirements) {
    newBooking.dietaryRequirements = [];
  }

  // Remove notes if not included
  if (!options.includeNotes) {
    newBooking.notes = undefined;
  }

  // Process comments
  processComments(newBooking, options.includeComments);

  // Clear transactional data
  clearTransactionalData(newBooking);

  return newBooking;
};

export default {
  copyDayBookings
};
